const ProductDescriptionToggle = (() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const OVERFLOW_THRESHOLD = 8;

  const getCollapsedHeight = (root) => {
    const raw = getComputedStyle(root).getPropertyValue('--product-description-collapsed-height').trim();
    if (!raw) return 120;

    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return 120;

    if (raw.endsWith('rem')) {
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      return value * rootFontSize;
    }

    if (raw.endsWith('em')) {
      const fontSize = parseFloat(getComputedStyle(root).fontSize) || 16;
      return value * fontSize;
    }

    return value;
  };

  const setLabel = (toggle, expanded) => {
    const label = toggle.querySelector('[data-product-description-label]');
    if (!label) return;
    label.textContent = expanded
      ? (theme.strings.seeLess || 'Show less')
      : (theme.strings.seeMore || 'Show more');
    toggle.setAttribute('aria-expanded', String(expanded));
  };

  const finishOpen = (root, clip) => {
    clip.style.transition = 'none';
    clip.style.height = 'auto';
    clip.offsetHeight;
    root.classList.add('is-expanded');
    root.classList.remove('is-opening');
    requestAnimationFrame(() => {
      clip.style.transition = '';
    });
  };

  const finishClose = (root, clip, collapsedHeight) => {
    clip.style.transition = 'none';
    clip.style.height = `${collapsedHeight}px`;
    clip.offsetHeight;
    root.classList.remove('is-closing');
    requestAnimationFrame(() => {
      clip.style.transition = '';
    });
  };

  const open = (root, clip, inner, toggle) => {
    const collapsedHeight = getCollapsedHeight(root);
    const endHeight = inner.scrollHeight;

    root.classList.add('is-opening');
    clip.style.height = `${collapsedHeight}px`;
    clip.style.overflow = 'hidden';
    setLabel(toggle, true);

    if (prefersReducedMotion) {
      finishOpen(root, clip);
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        clip.style.height = `${endHeight}px`;
      });
    });

    const onTransitionEnd = (event) => {
      if (event.target !== clip || event.propertyName !== 'height') return;
      clip.removeEventListener('transitionend', onTransitionEnd);
      finishOpen(root, clip);
    };

    clip.addEventListener('transitionend', onTransitionEnd);
  };

  const close = (root, clip, toggle) => {
    const collapsedHeight = getCollapsedHeight(root);
    const startHeight = clip.scrollHeight;

    root.classList.add('is-closing');
    root.classList.remove('is-expanded');
    clip.style.height = `${startHeight}px`;
    clip.style.overflow = 'hidden';
    setLabel(toggle, false);

    if (prefersReducedMotion) {
      finishClose(root, clip, collapsedHeight);
      return;
    }

    requestAnimationFrame(() => {
      clip.style.height = `${collapsedHeight}px`;
    });

    const onTransitionEnd = (event) => {
      if (event.target !== clip || event.propertyName !== 'height') return;
      clip.removeEventListener('transitionend', onTransitionEnd);
      finishClose(root, clip, collapsedHeight);
    };

    clip.addEventListener('transitionend', onTransitionEnd);
  };

  const setup = (root) => {
    if (root.getAttribute('data-product-description-init') === 'true') return;

    const clip = root.querySelector('[data-product-description-clip]');
    const inner = root.querySelector('[data-product-description-inner]');
    const toggle = root.querySelector('[data-product-description-toggle]');

    if (!clip || !inner || !toggle) return;

    root.setAttribute('data-product-description-init', 'true');

    const collapsedHeight = getCollapsedHeight(root);
    const needsToggle = inner.scrollHeight > collapsedHeight + OVERFLOW_THRESHOLD;

    if (!needsToggle) {
      toggle.hidden = true;
      return;
    }

    root.classList.add('is-collapsible');
    clip.style.height = `${collapsedHeight}px`;
    toggle.hidden = false;
    setLabel(toggle, false);

    toggle.addEventListener('click', () => {
      if (root.classList.contains('is-opening') || root.classList.contains('is-closing')) {
        return;
      }

      if (root.classList.contains('is-expanded')) {
        close(root, clip, toggle);
      } else {
        open(root, clip, inner, toggle);
      }
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-product-description]').forEach(setup);
  };

  return { init };
})();

const updatePickerLabelOnHover = (labelSelector, variantSelector) => {
  const currentLabel = document.querySelector(labelSelector);
  const variants = document.querySelectorAll(variantSelector);

  if (!currentLabel || !variants.length) return;

  const defaultLabel = currentLabel.textContent;

  variants.forEach(variant => {
    const label = variant.getAttribute("data-label");

    variant.addEventListener("mouseenter", () => {
      if (label) currentLabel.textContent = label;
    });

    variant.addEventListener("mouseleave", () => {
      currentLabel.textContent = defaultLabel;
    });
  });
};

const updateColorLabelOnHover = () => {
  updatePickerLabelOnHover("[data-current-label]", ".product-variant:not(.product-variant--size)");
};

const updateSizeLabelOnHover = () => {
  updatePickerLabelOnHover("[data-current-size-label]", ".product-variant--size");
};

const updateBundleTitleOnHover = () => {
  const currentTitle = document.querySelector("[data-current-item-title]");
  const bundleItems = document.querySelectorAll(".bundles-container__img");

  if (!currentTitle || !bundleItems.length) return;

  const defaultTitle = currentTitle.textContent;

  bundleItems.forEach(item => {
    const title = item.getAttribute("data-product-title");

    item.addEventListener("mouseenter", () => {
      if (title) currentTitle.textContent = title;
    });

    item.addEventListener("mouseleave", () => {
      currentTitle.textContent = defaultTitle;
    });
  });
};

const HighlightsSlider = (() => {
  let slider = null;

  const init = () => {
    const wrapper = document.querySelector('.highlights-grid');
    if (!wrapper) return;

    const items = wrapper.querySelectorAll('.highlights-grid__item');
    const itemCount = items.length;
    if (itemCount <= 1) return;

    const isMobile = window.innerWidth <= 768;
    const shouldInitOnDesktop = itemCount >= 5;

    // ✅ Initialize conditions
    if ((isMobile || shouldInitOnDesktop) && !slider) {
      slider = new Flickity(wrapper, {
        adaptiveHeight: false,
        initialIndex: 0,
        pageDots: true,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        groupCells: true,
        contain: true,
      });
    } 
    // ✅ Destroy when not mobile and less than 5 items
    else if (!isMobile && itemCount < 5 && slider) {
      slider.destroy();
      slider = null;
    }
  };

  // Optional: debounce resize for performance
  const resizeHandler = () => {
    clearTimeout(window._highlightsSliderTimeout);
    window._highlightsSliderTimeout = setTimeout(() => {
      init();
    }, 150);
  };

  const setup = () => {
    init();
    window.addEventListener('resize', resizeHandler);
  };

  return { setup };
})();

const InstructionsSlider = (() => {
  let slider = null;

  const init = () => {
    const wrapper = document.querySelector('.instructions-grid');
    if (!wrapper) return;

    const items = wrapper.querySelectorAll('.instructions-grid__item');
    const itemCount = items.length;
    if (itemCount <= 1) return;

    const isMobile = window.innerWidth <= 768;
    const shouldInitOnDesktop = itemCount > 4;

    if ((isMobile || shouldInitOnDesktop) && !slider) {
      slider = new Flickity(wrapper, {
        adaptiveHeight: false,
        initialIndex: 0,
        pageDots: true,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        groupCells: true,
        contain: true,
      });
    } else if (!isMobile && itemCount <= 4 && slider) {
      slider.destroy();
      slider = null;
    }
  };

  const resizeHandler = () => {
    clearTimeout(window._instructionsSliderTimeout);
    window._instructionsSliderTimeout = setTimeout(() => {
      init();
    }, 150);
  };

  const setup = () => {
    init();
    window.addEventListener('resize', resizeHandler);
  };

  return { setup };
})();

const WarrantyHighlightsSlider = (() => {
  let slider = null;

  const init = () => {
    const wrapper = document.querySelector('.warranty-section__highlights');
    if (!wrapper) return;

    const items = wrapper.querySelectorAll('.warranty-section__highlight');
    const itemCount = items.length;
    if (itemCount <= 1) return;

    const isMobile = window.innerWidth <= 768;

    if (isMobile && !slider) {
      slider = new Flickity(wrapper, {
        adaptiveHeight: false,
        initialIndex: 0,
        pageDots: true,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        groupCells: false,
        contain: true,
      });
    } else if (!isMobile && slider) {
      slider.destroy();
      slider = null;
    }
  };

  const resizeHandler = () => {
    clearTimeout(window._warrantyHighlightsSliderTimeout);
    window._warrantyHighlightsSliderTimeout = setTimeout(() => {
      init();
    }, 150);
  };

  const setup = () => {
    init();
    window.addEventListener('resize', resizeHandler);
  };

  return { setup };
})();


const ProductFaqAccordion = (() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const finishOpen = (details, content) => {
    content.style.transition = 'none';
    content.style.height = `${content.scrollHeight}px`;
    content.offsetHeight;
    content.style.removeProperty('height');
    content.style.overflow = '';
    details.classList.remove('is-opening');
    requestAnimationFrame(() => {
      content.style.transition = '';
    });
  };

  const finishClose = (details, content) => {
    content.style.transition = 'none';
    details.removeAttribute('open');
    content.style.removeProperty('height');
    content.style.overflow = '';
    details.classList.remove('is-closing');
    requestAnimationFrame(() => {
      content.style.transition = '';
    });
  };

  const openItem = (details, content) => {
    details.setAttribute('open', '');
    details.classList.add('is-opening');
    content.style.height = '0px';
    content.style.overflow = 'hidden';

    if (prefersReducedMotion) {
      finishOpen(details, content);
      return;
    }

    requestAnimationFrame(() => {
      const endHeight = content.scrollHeight;

      requestAnimationFrame(() => {
        content.style.height = `${endHeight}px`;
      });
    });

    const onTransitionEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'height') return;
      content.removeEventListener('transitionend', onTransitionEnd);
      finishOpen(details, content);
    };

    content.addEventListener('transitionend', onTransitionEnd);
  };

  const closeItem = (details, content) => {
    if (prefersReducedMotion) {
      finishClose(details, content);
      return;
    }

    details.classList.add('is-closing');
    const startHeight = content.scrollHeight;
    content.style.height = `${startHeight}px`;
    content.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      content.style.height = '0px';
    });

    const onTransitionEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'height') return;
      content.removeEventListener('transitionend', onTransitionEnd);
      finishClose(details, content);
    };

    content.addEventListener('transitionend', onTransitionEnd);
  };

  const init = () => {
    document.querySelectorAll('.product-faq__item:not([data-faq-init])').forEach((details) => {
      const summary = details.querySelector('.product-faq__trigger');
      const content = details.querySelector('.product-faq__content');

      if (!summary || !content) return;

      details.setAttribute('data-faq-init', 'true');

      summary.addEventListener('click', (event) => {
        event.preventDefault();

        if (details.classList.contains('is-opening') || details.classList.contains('is-closing')) {
          return;
        }

        if (details.hasAttribute('open')) {
          closeItem(details, content);
        } else {
          openItem(details, content);
        }
      });
    });
  };

  return { init };
})();

const UgcVideos = (() => {
  const SWIPE_THRESHOLD = 60;
  const DESKTOP_MIN = 769;
  const MODAL_ANIMATION_MS = 350;

  class UgcVideosInstance {
    constructor(root) {
      this.root = root;
      this.modal = root.querySelector('.ugc-videos__modal');
      this.sliderEl = root.querySelector('.ugc-videos__slider');
      this.cards = [...root.querySelectorAll('[data-ugc-card]')];
      this.slides = this.modal ? [...this.modal.querySelectorAll('[data-ugc-modal-slide]')] : [];
      this.productLists = this.modal ? [...this.modal.querySelectorAll('[data-ugc-products-list]')] : [];
      this.videoCount = parseInt(root.dataset.videoCount, 10) || this.slides.length;
      this.activeIndex = 0;
      this.isModalOpen = false;
      this.isModalClosing = false;
      this.isMuted = true;
      this.slider = null;
      this.productsSliders = new Map();
      this.lastFocus = null;
      this.touchStartY = 0;
      this.touchStartX = 0;
      this.chatElements = null;

      if (this.modal && this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }

      this.bindEvents();
      this.initSlider();
      this.initAjaxForms();
      this.onResize = this.onResize.bind(this);
      window.addEventListener('resize', this.onResize);
    }

    bindEvents() {
      this.cards.forEach((card) => {
        const video = card.querySelector('.ugc-videos__video');
        const playBtn = card.querySelector('.ugc-videos__play-btn');
        const muteBtn = card.querySelector('.ugc-videos__mute-btn');

        card.addEventListener('click', (event) => {
          if (event.target.closest('.ugc-videos__mute-btn')) return;
          const index = parseInt(card.dataset.index, 10);
          this.openModal(index);
        });

        card.addEventListener('mouseenter', () => {
          if (!this.isDesktop() || !video) return;
          card.classList.add('is-playing');
          if (muteBtn) muteBtn.hidden = false;
          video.muted = true;
          video.play().catch(() => {});
        });

        card.addEventListener('mouseleave', () => {
          if (!video) return;
          card.classList.remove('is-playing');
          video.pause();
          video.currentTime = 0;
          video.muted = true;
          if (muteBtn) {
            muteBtn.hidden = true;
            this.updateMuteButton(muteBtn, true);
          }
        });

        if (muteBtn && video) {
          muteBtn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            video.muted = !video.muted;
            this.updateMuteButton(muteBtn, video.muted);
          });
        }
      });

      this.modal.querySelectorAll('[data-ugc-modal-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeModal());
      });

      const modalInner = this.modal.querySelector('.ugc-videos__modal-inner');
      if (modalInner) {
        modalInner.addEventListener('click', (event) => {
          if (!this.isDesktop() || !this.isModalOpen || this.isModalClosing) return;
          if (event.target.closest('.ugc-videos__modal-stage')) return;
          this.closeModal();
        });
      }

      const prevBtn = this.modal.querySelector('[data-ugc-prev]');
      const nextBtn = this.modal.querySelector('[data-ugc-next]');
      if (prevBtn) prevBtn.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.goTo(this.activeIndex + 1));

      const modalMuteBtn = this.modal.querySelector('[data-ugc-modal-mute]');
      if (modalMuteBtn) {
        modalMuteBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.isMuted = !this.isMuted;
          this.applyModalMute();
          this.updateMuteButton(modalMuteBtn, this.isMuted);
        });
      }

      const swipeArea = this.modal.querySelector('[data-ugc-swipe-area]');
      if (swipeArea) {
        swipeArea.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        swipeArea.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
        swipeArea.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
      }

      this.modal.querySelectorAll('.ugc-videos__product-form').forEach((form) => {
        form.addEventListener('click', (event) => event.stopPropagation());
        form.addEventListener('submit', (event) => event.stopPropagation());
      });

      document.addEventListener('keydown', this.onKeyDown.bind(this));
    }

    onKeyDown(event) {
      if (!this.isModalOpen) return;
      if (event.key === 'Escape') this.closeModal();
      if (event.key === 'ArrowLeft') this.goTo(this.activeIndex - 1);
      if (event.key === 'ArrowRight') this.goTo(this.activeIndex + 1);
    }

    onTouchStart(event) {
      if (!this.isModalOpen || this.isDesktop()) return;
      const touch = event.touches[0];
      this.touchStartY = touch.clientY;
      this.touchStartX = touch.clientX;
    }

    onTouchMove(event) {
      if (!this.isModalOpen || this.isDesktop()) return;
      const touch = event.touches[0];
      const deltaY = Math.abs(touch.clientY - this.touchStartY);
      const deltaX = Math.abs(touch.clientX - this.touchStartX);
      if (deltaY > deltaX && deltaY > 10) {
        event.preventDefault();
      }
    }

    onTouchEnd(event) {
      if (!this.isModalOpen || this.isDesktop()) return;
      const touch = event.changedTouches[0];
      const deltaY = touch.clientY - this.touchStartY;
      const deltaX = touch.clientX - this.touchStartX;
      if (Math.abs(deltaY) < SWIPE_THRESHOLD || Math.abs(deltaY) < Math.abs(deltaX)) return;
      if (deltaY > 0) this.goTo(this.activeIndex + 1);
      else this.goTo(this.activeIndex - 1);
    }

    isDesktop() {
      return window.innerWidth >= DESKTOP_MIN;
    }

    initSlider() {
      if (!this.sliderEl || this.videoCount <= 1) {
        this.destroySlider();
        return;
      }

      if (this.isDesktop()) {
        this.destroySlider();
        return;
      }

      if (this.slider) return;

      this.slider = new Flickity(this.sliderEl, {
        adaptiveHeight: false,
        initialIndex: 0,
        pageDots: true,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        contain: true,
      });
    }

    destroySlider() {
      if (!this.slider) return;
      this.slider.destroy();
      this.slider = null;
    }

    onResize() {
      this.initSlider();
    }

    updateMuteButton(button, isMuted) {
      button.setAttribute('aria-pressed', String(isMuted));
      button.setAttribute(
        'aria-label',
        isMuted ? 'Unmute video' : 'Mute video'
      );
      const mutedIcon = button.querySelector('.ugc-videos__mute-icon--muted');
      const unmutedIcon = button.querySelector('.ugc-videos__mute-icon--unmuted');
      if (mutedIcon) mutedIcon.hidden = !isMuted;
      if (unmutedIcon) unmutedIcon.hidden = isMuted;
    }

    suppressShopifyChat() {
      const elements = [
        document.getElementById('ShopifyChat'),
        ...document.querySelectorAll('inbox-online-store-chat'),
      ].filter(Boolean);

      this.chatElements = elements;
      elements.forEach((el) => {
        if (el.dataset.ugcChatZIndex === undefined) {
          el.dataset.ugcChatZIndex = el.style.zIndex || '';
        }
        el.style.setProperty('z-index', '2147483646', 'important');
      });
    }

    restoreShopifyChat() {
      (this.chatElements || []).forEach((el) => {
        if (el.dataset.ugcChatZIndex !== undefined) {
          if (el.dataset.ugcChatZIndex) {
            el.style.zIndex = el.dataset.ugcChatZIndex;
          } else {
            el.style.removeProperty('z-index');
          }
          delete el.dataset.ugcChatZIndex;
        }
      });
      this.chatElements = null;
    }

    openModal(index) {
      if (!this.modal || this.isModalOpen || this.isModalClosing) return;
      this.lastFocus = document.activeElement;
      this.isModalOpen = true;
      this.activeIndex = index;
      this.modal.hidden = false;
      this.modal.classList.remove('is-open');
      document.body.classList.add('ugc-videos-modal-open');
      this.suppressShopifyChat();
      this.showSlide(index);
      this.initAjaxForms();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.modal.classList.add('is-open');
        });
      });

      const closeBtn = this.modal.querySelector(
        this.isDesktop() ? '.ugc-videos__modal-close--desktop' : '.ugc-videos__modal-close--mobile'
      );
      if (closeBtn) closeBtn.focus();
    }

    finishCloseModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.modal.classList.remove('is-open');
      this.isModalClosing = false;
      document.body.classList.remove('ugc-videos-modal-open');
      this.restoreShopifyChat();
      this.pauseAllModalVideos();
      this.destroyMobileProductsSliders();
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    closeModal() {
      if (!this.modal || !this.isModalOpen || this.isModalClosing) return;
      this.isModalOpen = false;
      this.isModalClosing = true;
      this.modal.classList.remove('is-open');

      const inner = this.modal.querySelector('.ugc-videos__modal-inner');
      let didFinish = false;
      const finish = () => {
        if (didFinish) return;
        didFinish = true;
        if (inner) inner.removeEventListener('transitionend', onTransitionEnd);
        this.finishCloseModal();
      };

      const onTransitionEnd = (event) => {
        if (event.target !== inner || event.propertyName !== 'transform') return;
        finish();
      };

      if (inner) {
        inner.addEventListener('transitionend', onTransitionEnd);
      }

      window.setTimeout(finish, MODAL_ANIMATION_MS + 50);
    }

    initAjaxForms() {
      if (typeof theme === 'undefined' || !theme.AjaxProduct) return;

      this.modal.querySelectorAll('.ugc-videos__product-form:not([data-ajax-init])').forEach((form) => {
        new theme.AjaxProduct(form, '[data-add-to-cart]');
        form.setAttribute('data-ajax-init', 'true');
      });
    }

    pauseAllModalVideos() {
      this.slides.forEach((slide) => {
        const video = slide.querySelector('.ugc-videos__video--modal');
        if (!video) return;
        video.pause();
        video.currentTime = 0;
        video.onended = null;
      });
    }

    getWrappedIndex(index) {
      if (this.videoCount <= 0) return 0;
      return (index + this.videoCount) % this.videoCount;
    }

    goTo(index) {
      const nextIndex = this.getWrappedIndex(index);
      if (nextIndex === this.activeIndex) {
        if (this.videoCount === 1) {
          this.replayActiveVideo();
        }
        return;
      }
      this.activeIndex = nextIndex;
      this.showSlide(nextIndex);
    }

    replayActiveVideo() {
      const activeSlide = this.slides[this.activeIndex];
      if (!activeSlide) return;
      const video = activeSlide.querySelector('.ugc-videos__video--modal');
      if (!video) return;
      video.currentTime = 0;
      video.muted = this.isMuted;
      video.play().catch(() => {});
    }

    showSlide(index) {
      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        slide.hidden = !isActive;
        const video = slide.querySelector('.ugc-videos__video--modal');
        if (video) {
          video.pause();
          video.currentTime = 0;
          video.onended = null;
          if (!isActive) return;
          video.muted = this.isMuted;
          video.onended = () => this.goTo(index + 1);
          video.play().catch(() => {});
        }
      });

      this.productLists.forEach((list, i) => {
        const isActive = i === index;
        list.classList.toggle('is-active', isActive);
        list.hidden = !isActive;
      });

      this.initMobileProductsSlider(index);
      this.applyModalMute();
    }

    applyModalMute() {
      const modalMuteBtn = this.modal.querySelector('[data-ugc-modal-mute]');
      if (modalMuteBtn) this.updateMuteButton(modalMuteBtn, this.isMuted);

      const activeSlide = this.slides[this.activeIndex];
      if (!activeSlide) return;
      const video = activeSlide.querySelector('.ugc-videos__video--modal');
      if (video) video.muted = this.isMuted;
    }

    destroyMobileProductsSliders() {
      this.productsSliders.forEach((slider) => {
        slider.element.querySelectorAll('.ugc-videos__modal-products-cell:not(.ugc-videos__modal-products-cell--gutter)').forEach((cell) => {
          cell.style.width = '';
          cell.style.marginLeft = '';
          cell.style.marginRight = '';
        });
        slider.element.querySelectorAll('.ugc-videos__modal-products-cell--gutter').forEach((gutter) => gutter.remove());
        slider.destroy();
      });
      this.productsSliders.clear();
    }

    initMobileProductsSlider(index) {
      if (this.isDesktop()) return;

      this.destroyMobileProductsSliders();

      const slide = this.slides[index];
      if (!slide) return;
      const sliderEl = slide.querySelector('[data-ugc-products-slider]');
      if (!sliderEl) return;

      const productCells = [...sliderEl.querySelectorAll(':scope > .ugc-videos__modal-products-cell:not(.ugc-videos__modal-products-cell--gutter)')];
      if (productCells.length <= 1) return;

      const gap = 12;
      const leadingGutter = document.createElement('div');
      leadingGutter.className = 'ugc-videos__modal-products-cell ugc-videos__modal-products-cell--gutter';
      leadingGutter.setAttribute('aria-hidden', 'true');

      const trailingGutter = document.createElement('div');
      trailingGutter.className = 'ugc-videos__modal-products-cell ugc-videos__modal-products-cell--gutter';
      trailingGutter.setAttribute('aria-hidden', 'true');

      sliderEl.insertBefore(leadingGutter, sliderEl.firstChild);
      sliderEl.appendChild(trailingGutter);

      productCells.forEach((cell) => {
        cell.style.width = '85%';
        cell.style.marginRight = `${gap}px`;
        cell.style.marginLeft = '';
      });

      const slider = new Flickity(sliderEl, {
        adaptiveHeight: false,
        pageDots: false,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        contain: true,
        initialIndex: 0,
      });

      this.productsSliders.set(index, slider);
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('ugc-videos:not([data-ugc-init])').forEach((root) => {
      root.setAttribute('data-ugc-init', 'true');
      new UgcVideosInstance(root);
    });
  };

  return { init };
})();


const PropelFormI18n = (() => {
  // TEMP: force Polish for testing both Propel forms
  const FORCE_LOCALE = 'pl';

  const STRINGS = {
    pl: {
      'Product Registration – 10-Year Warranty': 'Rejestracja produktu – 10 lat gwarancji',
      'Warranty Support': 'Wsparcie gwarancyjne',
      'Choose a product': 'Wybierz produkt',
      'Search for your product here': 'Wyszukaj swój produkt',
      'Serial number': 'Numer seryjny',
      'Store/Seller name': 'Nazwa sklepu/sprzedawcy',
      'Document number': 'Numer dokumentu',
      'Purchase date': 'Data zakupu',
      'First name': 'Imię',
      'Last name': 'Nazwisko',
      Country: 'Kraj',
      Email: 'E-mail',
      Next: 'Dalej',
      Back: 'Wstecz',
      Submit: 'Wyślij',
      Register: 'Zarejestruj',
      'Submit claim': 'Wyślij zgłoszenie',
      Required: 'Wymagane',
      'Incorrect number': 'Nieprawidłowy numer',
      'Invalid number': 'Nieprawidłowy numer',
      'Registration successful': 'Rejestracja zakończona sukcesem',
      'Your product has been registered successfully.': 'Twój produkt został pomyślnie zarejestrowany.',
      'Claim submitted': 'Zgłoszenie wysłane',
      'Your claim has been submitted.': 'Twoje zgłoszenie zostało wysłane.',

      'Register your Masuria Wild product and enjoy a 10-year warranty!':
        'Zarejestruj produkt Masuria Wild i korzystaj z 10-letniej gwarancji!',
      'Fill out the form below to activate your warranty and receive exclusive offers for Masuria Wild customers.':
        'Wypełnij formularz poniżej, aby aktywować gwarancję i otrzymywać ekskluzywne oferty dla klientów Masuria Wild.',
      "You'll find the serial number on the inner wall of your fire pit (see illustration below).":
        'Numer seryjny znajdziesz na wewnętrznej ściance paleniska (patrz ilustracja poniżej).',
      'All required fields must be completed.': 'Wszystkie wymagane pola muszą zostać uzupełnione.',
      'You will receive a confirmation email within 2–3 business days.':
        'Potwierdzenie e-mailem otrzymasz w ciągu 2–3 dni roboczych.',
      'If you need help in another language or have any questions, just write to us via the chat in the bottom right corner of the screen.':
        'Jeśli potrzebujesz pomocy w innym języku lub masz pytania, napisz do nas przez czat w prawym dolnym rogu ekranu.',

      "We're here to help!": 'Jesteśmy tu, aby pomóc!',
      'If you have a problem with your Masuria Wild product or need warranty support, please fill out the form below. Our team will review your request and get back to you as soon as possible.':
        'Jeśli masz problem z produktem Masuria Wild lub potrzebujesz wsparcia gwarancyjnego, wypełnij formularz poniżej. Nasz zespół przeanalizuje zgłoszenie i skontaktuje się z Tobą tak szybko, jak to możliwe.',
    },
  };

  // Full multiline intros from the Propel API (kept as separate keys).
  STRINGS.pl[
    "Register your Masuria Wild product and enjoy a 10-year warranty!\nFill out the form below to activate your warranty and receive exclusive offers for Masuria Wild customers.\n\nYou'll find the serial number on the inner wall of your fire pit (see illustration below).\nAll required fields must be completed.\nYou will receive a confirmation email within 2–3 business days.\n\nIf you need help in another language or have any questions, just write to us via the chat in the bottom right corner of the screen."
  ] =
    'Zarejestruj produkt Masuria Wild i korzystaj z 10-letniej gwarancji!\nWypełnij formularz poniżej, aby aktywować gwarancję i otrzymywać ekskluzywne oferty dla klientów Masuria Wild.\n\nNumer seryjny znajdziesz na wewnętrznej ściance paleniska (patrz ilustracja poniżej).\nWszystkie wymagane pola muszą zostać uzupełnione.\nPotwierdzenie e-mailem otrzymasz w ciągu 2–3 dni roboczych.\n\nJeśli potrzebujesz pomocy w innym języku lub masz pytania, napisz do nas przez czat w prawym dolnym rogu ekranu.';

  STRINGS.pl[
    "We're here to help!\nIf you have a problem with your Masuria Wild product or need warranty support, please fill out the form below. Our team will review your request and get back to you as soon as possible.\n\nAll required fields must be completed.\nYou will receive a confirmation email within 2–3 business days.\n\nIf you need help in another language or have any questions, just write to us via the chat in the bottom right corner of the screen."
  ] =
    'Jesteśmy tu, aby pomóc!\nJeśli masz problem z produktem Masuria Wild lub potrzebujesz wsparcia gwarancyjnego, wypełnij formularz poniżej. Nasz zespół przeanalizuje zgłoszenie i skontaktuje się z Tobą tak szybko, jak to możliwe.\n\nWszystkie wymagane pola muszą zostać uzupełnione.\nPotwierdzenie e-mailem otrzymasz w ciągu 2–3 dni roboczych.\n\nJeśli potrzebujesz pomocy w innym języku lub masz pytania, napisz do nas przez czat w prawym dolnym rogu ekranu.';

  const normalize = (value) =>
    (value || '')
      .replace(/\u00a0/g, ' ')
      .replace(/[\u2018\u2019\u02BC]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[–—]/g, '-')
      .replace(/\r\n/g, '\n')
      .trim();

  const normalizeLoose = (value) => normalize(value).replace(/\s+/g, ' ');

  const buildMaps = (strings) => {
    const exact = {};
    const loose = {};
    Object.entries(strings).forEach(([k, v]) => {
      exact[normalize(k)] = v;
      loose[normalizeLoose(k)] = v;
    });
    return { exact, loose };
  };

  const MAPS = Object.fromEntries(
    Object.entries(STRINGS).map(([locale, strings]) => [locale, buildMaps(strings)])
  );

  const getLocale = () => {
    if (FORCE_LOCALE) return FORCE_LOCALE;
    const lang = (document.documentElement.lang || window.Shopify?.locale || 'en').toLowerCase();
    return lang.split('-')[0];
  };

  const translateValue = (value, maps) => {
    if (typeof value !== 'string' || !value) return value;
    const exact = maps.exact[normalize(value)];
    if (exact) return exact;
    const loose = maps.loose[normalizeLoose(value)];
    if (loose) return loose;

    // Translate multiline blobs line-by-line while keeping blank lines.
    if (value.includes('\n')) {
      return value
        .split('\n')
        .map((line) => {
          const trimmed = line.trim();
          if (!trimmed) return line;
          const hit = maps.exact[normalize(trimmed)] || maps.loose[normalizeLoose(trimmed)];
          return hit ? line.replace(trimmed, hit) : line;
        })
        .join('\n');
    }

    return value;
  };

  const translateDeep = (value, maps) => {
    if (typeof value === 'string') return translateValue(value, maps);
    if (Array.isArray(value)) return value.map((item) => translateDeep(item, maps));
    if (value && typeof value === 'object') {
      Object.keys(value).forEach((key) => {
        value[key] = translateDeep(value[key], maps);
      });
    }
    return value;
  };

  const isFormApiUrl = (url) => /\/a\/pr\/api\/storefront\/forms\/\d+/i.test(String(url || ''));

  const installNetworkPatch = () => {
    if (window.__propelFormI18nFetchPatched) return;
    window.__propelFormI18nFetchPatched = true;

    const maps = MAPS[getLocale()];
    if (!maps) return;

    if (typeof window.fetch === 'function') {
      const originalFetch = window.fetch.bind(window);
      window.fetch = async (...args) => {
        const response = await originalFetch(...args);
        try {
          const requestUrl = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
          if (!isFormApiUrl(requestUrl) || !response.ok) return response;

          const data = await response.clone().json();
          translateDeep(data, maps);

          return new Response(JSON.stringify(data), {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
          });
        } catch (error) {
          return response;
        }
      };
    }

    // Propel uses axios, which reads XMLHttpRequest.responseText.
    // Patch the getter so translation happens before axios parses JSON.
    if (typeof XMLHttpRequest !== 'undefined') {
      const originalOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this.__propelFormI18nUrl = url;
        this.__propelFormI18nTranslated = null;
        return originalOpen.call(this, method, url, ...rest);
      };

      const responseTextDesc = Object.getOwnPropertyDescriptor(XMLHttpRequest.prototype, 'responseText');
      if (responseTextDesc && responseTextDesc.get) {
        const originalResponseTextGet = responseTextDesc.get;
        Object.defineProperty(XMLHttpRequest.prototype, 'responseText', {
          configurable: true,
          enumerable: responseTextDesc.enumerable,
          get: function () {
            const text = originalResponseTextGet.call(this);
            if (
              this.readyState === 4 &&
              this.status >= 200 &&
              this.status < 300 &&
              isFormApiUrl(this.__propelFormI18nUrl)
            ) {
              if (this.__propelFormI18nTranslated) return this.__propelFormI18nTranslated;
              try {
                const data = translateDeep(JSON.parse(text), maps);
                this.__propelFormI18nTranslated = JSON.stringify(data);
                return this.__propelFormI18nTranslated;
              } catch (error) {
                return text;
              }
            }
            return text;
          },
        });
      }

      const responseDesc = Object.getOwnPropertyDescriptor(XMLHttpRequest.prototype, 'response');
      if (responseDesc && responseDesc.get) {
        const originalResponseGet = responseDesc.get;
        Object.defineProperty(XMLHttpRequest.prototype, 'response', {
          configurable: true,
          enumerable: responseDesc.enumerable,
          get: function () {
            if (
              this.readyState === 4 &&
              this.responseType !== 'blob' &&
              this.responseType !== 'arraybuffer' &&
              isFormApiUrl(this.__propelFormI18nUrl)
            ) {
              return this.responseText;
            }
            return originalResponseGet.call(this);
          },
        });
      }
    }
  };

  // Patch immediately when this file runs (must be before Propel/axios).
  if (/\/a\/pr\//.test(location.pathname)) {
    installNetworkPatch();
  }

  // Hardcoded Propel UI chrome (Register / Submit claim) is not in the forms API.
  const installDomPatch = (maps) => {
    if (window.__propelFormI18nDomPatched) return;
    window.__propelFormI18nDomPatched = true;

    const isIgnored = (node) => {
      const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
      return !el || !!el.closest('script, style, noscript, header, footer, nav, .mobile-nav, .toolbar, .site-header, .site-footer');
    };

    const translateTextNode = (node) => {
      const raw = node.nodeValue;
      if (!raw) return;
      const key = normalizeLoose(raw);
      const translated = maps.loose[key];
      if (!translated || key === normalizeLoose(translated)) return;
      const leading = (raw.match(/^\s*/) || [''])[0];
      const trailing = (raw.match(/\s*$/) || [''])[0];
      node.nodeValue = `${leading}${translated}${trailing}`;
    };

    const translateButtons = (root) => {
      root.querySelectorAll('button, [role="button"], a.Polaris-Button, .Polaris-Button').forEach((btn) => {
        if (isIgnored(btn)) return;
        const key = normalizeLoose(btn.textContent);
        const translated = maps.loose[key];
        if (!translated || key === normalizeLoose(translated)) return;

        // Prefer replacing leaf text nodes so Polaris structure stays intact.
        const walker = document.createTreeWalker(btn, NodeFilter.SHOW_TEXT);
        let node;
        let replaced = false;
        while ((node = walker.nextNode())) {
          if (normalizeLoose(node.nodeValue) === key) {
            node.nodeValue = translated;
            replaced = true;
            break;
          }
        }
        if (!replaced) btn.textContent = translated;
      });
    };

    const apply = () => {
      const roots = [
        document.querySelector('#__next'),
        document.getElementById('MainContent'),
        document.body,
      ].filter(Boolean);

      roots.forEach((root) => {
        translateButtons(root);

        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
          acceptNode(node) {
            if (!node.nodeValue || !normalizeLoose(node.nodeValue)) return NodeFilter.FILTER_REJECT;
            if (isIgnored(node)) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
          },
        });

        const nodes = [];
        while (walker.nextNode()) nodes.push(walker.currentNode);
        nodes.forEach(translateTextNode);
      });
    };

    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        apply();
      });
    };

    apply();
    const startObserver = () => {
      new MutationObserver(schedule).observe(document.documentElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      apply();
    };

    if (document.body) startObserver();
    else document.addEventListener('DOMContentLoaded', startObserver, { once: true });

    // Propel hydrates async; keep a short retry window.
    [300, 800, 1500, 3000, 5000].forEach((ms) => setTimeout(apply, ms));
  };

  const setup = () => {
    if (!/\/a\/pr\//.test(location.pathname)) return;
    installNetworkPatch();
    const maps = MAPS[getLocale()];
    if (maps) installDomPatch(maps);
  };

  // Also start DOM patch ASAP (not only on DOMContentLoaded).
  if (/\/a\/pr\//.test(location.pathname)) {
    const maps = MAPS[getLocale()];
    if (maps) installDomPatch(maps);
  }

  return { setup };
})();

const CartUpsell = (() => {
  let adding = false;

  const syncFromMarkup = (text) => {
    const html = document.createElement('div');
    html.innerHTML = text;
    const source = html.querySelector('[data-cart-upsell]');
    const targets = document.querySelectorAll('[data-cart-upsell]');
    if (!targets.length) return;

    const markup = source ? source.innerHTML : '';
    targets.forEach((target) => {
      target.innerHTML = markup;
    });
  };

  const patchCartMarkup = () => {
    if (typeof theme === 'undefined' || !theme.CartForm || !theme.CartForm.prototype) return;
    if (theme.CartForm.prototype.__cartUpsellPatched) return;

    const originalCartMarkup = theme.CartForm.prototype.cartMarkup;
    theme.CartForm.prototype.cartMarkup = function (text) {
      originalCartMarkup.call(this, text);
      syncFromMarkup(text);
    };
    theme.CartForm.prototype.__cartUpsellPatched = true;
  };

  const addToCart = (button) => {
    if (adding || !button || !theme.routes || !theme.routes.cartAdd) return;

    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    adding = true;
    button.classList.add('btn--loading');
    button.disabled = true;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    fetch(theme.routes.cartAdd, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 422) {
          if (data.description || data.message) {
            alert(data.description || data.message);
          }
          return;
        }

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: data,
            addToCartBtn: button,
          },
        }));

        if (document.body.classList.contains('template-cart')) {
          window.scrollTo(0, 0);
          window.location.reload();
          return;
        }

        if (theme.settings && theme.settings.cartType === 'page') {
          window.location = theme.routes.cartPage;
          return;
        }

        // Drawer cart rebuilds via the ajaxProduct:added listener on CartDrawer.
        // Also rebuild any page cart forms if present.
        document.dispatchEvent(new CustomEvent('cart:build'));
      })
      .catch(() => {})
      .finally(() => {
        adding = false;
        button.classList.remove('btn--loading');
        button.disabled = false;
      });
  };

  const onClick = (event) => {
    const button = event.target.closest('[data-cart-upsell-add]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    addToCart(button);
  };

  const init = () => {
    patchCartMarkup();
    document.addEventListener('click', onClick);
  };

  return { init };
})();

document.addEventListener("DOMContentLoaded", () => {
  ProductDescriptionToggle.init();
  updateColorLabelOnHover();
  updateSizeLabelOnHover();
  updateBundleTitleOnHover();
  HighlightsSlider.setup();
  InstructionsSlider.setup();
  WarrantyHighlightsSlider.setup();
  ProductFaqAccordion.init();
  UgcVideos.init();
  PropelFormI18n.setup();
  CartUpsell.init();
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-product-description]')) {
    ProductDescriptionToggle.init(event.target);
  }
  if (event.target.querySelector('.product-faq__list')) {
    ProductFaqAccordion.init();
  }
  if (event.target.querySelector('ugc-videos')) {
    UgcVideos.init(event.target);
  }
  if (event.target.querySelector('.warranty-section__highlights')) {
    WarrantyHighlightsSlider.setup();
  }
});